import { useMemo } from 'react';
import { X } from 'lucide-react';

import { useConversationContext } from '@/contexts/v2/ConversationContext';
import type { WidgetAction } from '@shared/conversation-schema';
import { cn } from '@/lib/utils';

import { MessageMarkdown } from './MessageMarkdown';

export type LastResponseCardProps = {
  /** Close affordance — fired by the top-right X button. Wires to "collapse to peek". */
  onClose?: () => void;
  className?: string;
};

/** Format an ISO timestamp into a short relative phrase ("just now", "2m ago", "3h ago"). */
function formatRelative(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

/**
 * Compact card showing Anaka's most recent assistant turn — the "detailed"
 * resting state between the slim peek pill and the full focused thread.
 *
 * Connected variant: reads the last assistant turn directly from
 * `useConversationContext()` so the shell can drop it in without prop
 * drilling. Returns `null` when there is no last assistant turn — in that
 * case the shell renders just the input bar.
 *
 * Mirrors the `LastResponseCard` in `design/.../screens/global-chat.jsx`.
 *
 * FR-008 (detailed-mode last-response card).
 */
export function LastResponseCard({ onClose, className }: LastResponseCardProps) {
  const { turns, sendMessage } = useConversationContext();

  const lastAssistantTurn = useMemo(
    () => [...turns].reverse().find((t) => t.role === 'assistant'),
    [turns],
  );

  // Full assistant prose — joined across text blocks with paragraph breaks so
  // markdown structure survives. No truncation here: detailed mode shows the
  // complete styled message (the slim peek pill keeps its own short snippet).
  const text = useMemo(() => {
    if (!lastAssistantTurn) return '';
    return lastAssistantTurn.content
      .filter((b): b is { type: 'text'; text: string } => b.type === 'text')
      .map((b) => b.text)
      .join('\n\n');
  }, [lastAssistantTurn]);

  /** Collect WidgetAction chips from all widget blocks in the last assistant turn. */
  const chips = useMemo<WidgetAction[]>(() => {
    if (!lastAssistantTurn) return [];
    return lastAssistantTurn.content.flatMap((b) =>
      b.type === 'widget' && b.actions && b.actions.length > 0 ? b.actions : [],
    );
  }, [lastAssistantTurn]);

  if (!lastAssistantTurn) return null;
  if (!text) return null;

  const time = formatRelative(lastAssistantTurn.timestamp);

  return (
    <div
      data-testid="last-response-card"
      className={cn('flex items-start gap-3 rounded-[14px] backdrop-blur-xl', className)}
      style={{
        background: 'var(--chat-surface)',
        border: '1px solid var(--chat-border)',
        boxShadow: 'var(--chat-shadow)',
        padding: '14px 16px',
        fontFamily: "'Open Sans', sans-serif",
      }}
    >
      {/* Gradient "A" avatar (md) */}
      <span
        aria-hidden
        data-testid="last-response-card-avatar"
        className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold text-white"
        style={{ background: 'linear-gradient(135deg, #7C3AED, #3B82F6)', flexShrink: 0 }}
      >
        A
      </span>

      <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 6 }}>
        {/* Header row */}
        <div className="flex items-baseline" style={{ gap: 8 }}>
          <span
            data-testid="last-response-card-name"
            style={{ fontSize: 12.5, fontWeight: 600, color: '#C084FC' }}
          >
            Anaka
          </span>

          {time ? (
            <span
              data-testid="last-response-card-timestamp"
              style={{ fontSize: 10.5, color: 'var(--foreground-subtle)' }}
            >
              {time}
            </span>
          ) : null}

          <span
            data-testid="last-response-card-tag"
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontFamily: 'var(--font-mono, "Fira Code", monospace)',
              color: 'var(--foreground-subtle)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Last response
          </span>
        </div>

        {/* Body — full markdown-formatted message. Capped height keeps the
            card on-screen; a long reply scrolls within the card instead of
            growing off the top of the viewport. */}
        <div
          data-testid="last-response-card-body"
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--foreground)',
            textWrap: 'pretty' as React.CSSProperties['textWrap'],
            margin: 0,
            maxHeight: '42vh',
            overflowY: 'auto',
          }}
        >
          <MessageMarkdown>{text}</MessageMarkdown>
        </div>

        {/* Reply chips row — rendered only when the turn carries widget actions */}
        {chips.length > 0 ? (
          <div
            data-testid="last-response-card-chips"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}
          >
            {chips.map((chip) => (
              <button
                key={chip.action}
                type="button"
                onClick={() => void sendMessage(chip.text ?? chip.label)}
                style={{
                  all: 'unset' as React.CSSProperties['all'],
                  cursor: 'pointer',
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: 'var(--foreground)',
                  background: 'var(--secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 9999,
                  padding: '4px 10px',
                  fontFamily: "'Open Sans', sans-serif",
                }}
              >
                {chip.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Close button — top-right, 24×24 */}
      {onClose ? (
        <button
          type="button"
          aria-label="Minimise last response"
          data-testid="last-response-card-close"
          onClick={onClose}
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            marginTop: 2,
            background: 'transparent',
            border: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted-foreground)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <X size={14} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
